import React, { useState, useEffect, useRef } from 'react';
import usePersistentToggle from './usePersistentToggle';

function McVragenSection({ questions, scores, onScoreChange, onUserAnswersChange }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewMode, setReviewMode] = useState('all'); // 'all' or 'incorrect'
  const [showIntro, toggleIntro] = usePersistentToggle('mc_vragen_intro', true);
  const [showTips, toggleTips] = usePersistentToggle('mc_vragen_tips', false);
  const [showOptions, setShowOptions] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [shuffledOptions, setShuffledOptions] = useState({});
  const [niveauFilter, setNiveauFilter] = useState('alle vragen');
  const [levelFilter, setLevelFilter] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);
  const [showRepeatOnly, setShowRepeatOnly] = useState(false);
  const [attemptCounts, setAttemptCounts] = useState({});
  const filtersRef = useRef(null);
  const vraagTitelRef = useRef(null);
  const [showAiWarningPopup, setShowAiWarningPopup] = useState(false);
  const aiWarningRef = useRef(null);

  // Filter logic for niveau
  const niveauOptions = [
    { label: 'Kennis', value: 'kennis' },
    { label: 'Begrip', value: 'begrip' },
    { label: 'Toepassing', value: 'toepassing' },
    { label: 'Kennis & Begrip', value: 'kennis & begrip' },
    { label: 'Alle vragen', value: 'alle vragen' }
  ];

  // Shuffle/filter questions telkens het hoofdstuk (questions), het niveaufilter of de herhaalmodus wijzigt
  useEffect(() => {
    let filtered = [...questions];
    if (niveauFilter !== 'alle vragen') {
      if (niveauFilter === 'kennis & begrip') {
        filtered = questions.filter(q => q.niveau === 'kennis' || q.niveau === 'begrip');
      } else {
        filtered = questions.filter(q => q.niveau === niveauFilter);
      }
    }
    if (showRepeatOnly) {
      filtered = filtered.filter(q => {
        const arr = scores[q.id] || [];
        return arr.length > 0 && arr[arr.length - 1].status === 'incorrect';
      });
    }
    const shuffled = [...filtered].sort(() => Math.random() - 0.5);
    setShuffledQuestions(shuffled);
    
    // Create shuffled options for each question
    const shuffledOpts = {};
    shuffled.forEach(q => {
      const options = [...q.options];
      const correctIndex = q.correctOptionIndex;
      const shuffledIndices = options.map((_, i) => i).sort(() => Math.random() - 0.5);
      shuffledOpts[q.id] = {
        options: shuffledIndices.map(i => options[i]),
        correctIndex: shuffledIndices.indexOf(correctIndex)
      };
    });
    setShuffledOptions(shuffledOpts);
    setCurrentQuestionIndex(0);

    // Reset antwoorden als je van filter wisselt (behalve als je in herhaalmodus blijft staan)
    setSelectedAnswers({});
    setShowOptions(false);
    setIsSubmitted(false);
    setShowReview(false);
  }, [questions, niveauFilter, showRepeatOnly]);

  useEffect(() => {
    if (typeof onUserAnswersChange === 'function') {
      onUserAnswersChange(selectedAnswers);
    }
  }, [selectedAnswers, onUserAnswersChange]);

  // Click-away listener voor AI waarschuwing popup
  useEffect(() => {
    if (!showAiWarningPopup) return;
    function handleClickOutside(event) {
      if (aiWarningRef.current && !aiWarningRef.current.contains(event.target) && !event.target.closest('button[aria-label="Waarschuwing AI-gegenereerde content"]')) {
        setShowAiWarningPopup(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAiWarningPopup]);

  const handleAnswerSelect = (questionId, optionIndex) => {
    if (!selectedAnswers[questionId]) {
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: optionIndex
      }));
      // Nieuw: voeg poging toe aan array, nu met answerIndex
      const prevAttempts = Array.isArray(scores[questionId]) ? scores[questionId] : [];
      const newAttempt = {
        status: optionIndex === shuffledOptions[questionId].correctIndex ? 'correct' : 'incorrect',
        timestamp: Date.now(),
        answerIndex: optionIndex
      };
      const newScores = {
        ...scores,
        [questionId]: [...prevAttempts, newAttempt]
      };
      onScoreChange(newScores);
      setAttemptCounts(prev => ({
        ...prev,
        [questionId]: (prev[questionId] || 0) + 1
      }));
      setTimeout(scrollToVraagTitel, 0);
    }
  };

  // Helper om naar filters te scrollen
  const scrollToFilters = () => {
    if (filtersRef.current) {
      filtersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper om naar de vraag-titel te scrollen
  const scrollToVraagTitel = () => {
    if (vraagTitelRef.current) {
      vraagTitelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      const nextQuestion = filteredQuestions[nextIndex];
      if (nextQuestion && selectedAnswers[nextQuestion.id] !== undefined) {
        setShowOptions(true);
      } else {
        setShowOptions(false);
      }
      setTimeout(scrollToFilters, 0);
    } else if (!showReview) {
      setIsSubmitted(true);
      setTimeout(scrollToFilters, 0);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      const prevQuestion = filteredQuestions[prevIndex];
      if (prevQuestion && selectedAnswers[prevQuestion.id] !== undefined) {
        setShowOptions(true);
      } else {
        setShowOptions(false);
      }
      setTimeout(scrollToFilters, 0);
    }
  };

  const startReview = (mode) => {
    setReviewMode(mode);
    setShowReview(true);
    setCurrentQuestionIndex(0);
    setShowOptions(false);
    setTimeout(scrollToFilters, 0);
  };

  const restartQuiz = () => {
    setSelectedAnswers({});
    setIsSubmitted(false);
    setShowReview(false);
    setCurrentQuestionIndex(0);
    setShowOptions(false);
    onScoreChange({});
    setTimeout(scrollToFilters, 0);
  };

  // Gebruik altijd de laatste poging voor status, filters en labels
  const lastAttempt = (qid) => {
    const arr = scores[qid] || [];
    return arr.length > 0 ? arr[arr.length - 1] : undefined;
  };

  const filteredQuestions = showReview && reviewMode === 'incorrect' 
    ? shuffledQuestions.filter((q) => lastAttempt(q.id)?.status === 'incorrect')
    : shuffledQuestions;

  const noQuestions = filteredQuestions.length === 0;

  // Bepaal of er überhaupt fout beantwoorde vragen beschikbaar zijn in de huidige niveauselectie
  const incorrectAvailable = questions.some(q => {
    let binnenFilter = true;
    if (niveauFilter !== 'alle vragen') {
      if (niveauFilter === 'kennis & begrip') {
        binnenFilter = q.niveau === 'kennis' || q.niveau === 'begrip';
      } else {
        binnenFilter = q.niveau === niveauFilter;
      }
    }
    return binnenFilter && lastAttempt(q.id)?.status === 'incorrect';
  });

  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const hasAnswered = currentQuestion && selectedAnswers[currentQuestion.id] !== undefined;
  const totalCorrect = Object.values(scores).filter(arr => Array.isArray(arr) && arr.length > 0 && arr[arr.length - 1].status === 'correct').length;
  const percentage = questions.length > 0 ? Math.round((totalCorrect / questions.length) * 100) : 0;

  const setCorrect = filteredQuestions.filter(q => lastAttempt(q.id)?.status === 'correct').length;
  const setTotal = filteredQuestions.length;
  const setPercentage = setTotal > 0 ? Math.round((setCorrect / setTotal) * 100) : 0;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-blue-700">MC Vragen</h2>
      
      <div className="mb-4">
        <button
          className="text-blue-700 font-semibold mb-2 focus:outline-none flex items-center gap-2"
          onClick={toggleIntro}
          aria-expanded={showIntro}
          aria-controls="mcvragen-intro"
        >
          {showIntro ? '▼' : '►'} Inleiding
        </button>
        {showIntro && (
          <div id="mcvragen-intro" className="bg-blue-50 p-6 rounded-lg shadow-sm">
            <p className="text-gray-700 leading-relaxed">
              Test je kennis met deze meerkeuzevragen! Dit onderdeel helpt je om te controleren of je de stof goed begrijpt en waar je nog extra aandacht aan moet besteden. De vragen variëren in moeilijkheidsgraad en dekken alle belangrijke aspecten van bindweefselherstel. Gebruik dit onderdeel regelmatig om je voortgang te meten en je kennis te verstevigen.
            </p>
          </div>
        )}
      </div>

      <div className="mb-4">
        <button
          className="text-blue-700 font-semibold mb-2 focus:outline-none flex items-center gap-2"
          onClick={toggleTips}
          aria-expanded={showTips}
          aria-controls="mcvragen-tips"
        >
          {showTips ? '▼' : '►'} Bekijk tips voor effectief oefenen
        </button>
        {showTips && (
          <div id="mcvragen-tips" className="bg-blue-50 p-6 rounded-lg shadow-sm mt-2">
            <strong>Tips voor effectief oefenen:</strong>
            <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-700">
              <li>Het uit je hoofd ophalen van het goede antwoord heeft een veel sterker leereffect dan het herkennen van antwoordopties.</li>
              <li>Neem even de tijd om goed na te denken over het antwoord voordat je de opties bekijkt.</li>
              <li>Dit voelt moeilijker, maar het is bewezen veel effectiever voor je lange termijn geheugen.</li>
            </ul>
          </div>
        )}
      </div>

      <div ref={filtersRef} className="mb-4 p-4 bg-gray-100 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Filters</h3>
        <div className="flex flex-wrap gap-2 items-start">
          {/* Niveau */}
          <div className="w-full sm:w-auto">
            <div className="text-xs font-semibold text-gray-500 mb-1 pl-1">Niveau</div>
            <div className="flex flex-wrap gap-2">
              {niveauOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setNiveauFilter(opt.value); setIsReviewing(false); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors border border-gray-300 whitespace-nowrap
                    ${niveauFilter === opt.value && !isReviewing
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 hover:bg-blue-100'}
                  `}
                  disabled={opt.value === ''}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {/* Status */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1 pl-1">Status</div>
            <div className="flex gap-2">
              <div className="relative group">
                <button
                  onClick={() => {
                    setShowRepeatOnly(prev => {
                      const newVal = !prev;
                      // Als we overschakelen naar herhaalmodus, wis antwoorden en opties
                      if (!prev) {
                        setSelectedAnswers({});
                        setShowOptions(false);
                        setIsSubmitted(false);
                        setShowReview(false);
                        setCurrentQuestionIndex(0);
                      }
                      return newVal;
                    });
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border border-gray-300 ${showRepeatOnly ? 'bg-orange-600 text-white border-orange-600' : (incorrectAvailable ? 'bg-white text-orange-800 hover:bg-orange-100 border-orange-300' : 'bg-white text-gray-400 border-gray-300')}`}
                  aria-label="Toon alleen te herhalen vragen"
                  type="button"
                >
                  Te herhalen vragen
                </button>
                <div className="absolute left-0 top-10 z-30 bg-white border border-orange-300 rounded-lg shadow-lg p-3 w-64 text-xs text-gray-800 hidden group-hover:block">
                  Toon alleen de vragen die je nog niet correct hebt beantwoord.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Setresultaat direct onder filters */}
      {(isSubmitted || showReview) && (
        <div className="mt-8 text-center p-6 bg-blue-50 rounded-lg">
          <div className="flex flex-col items-center gap-2 mb-4">
            <div className="text-lg font-semibold text-blue-700">
              Setresultaat: {setPercentage}% ({setCorrect} van de {setTotal} vragen correct)
            </div>
            <div className="text-sm text-gray-600">
              Totaalresultaat: {percentage}% ({totalCorrect} van de {questions.length} vragen correct)
            </div>
          </div>
          <div className="mb-2 text-xs text-gray-500">Acties voor deze set:</div>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => startReview('all')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              Bekijk alle vragen uit deze set
            </button>
            <button
              onClick={() => startReview('incorrect')}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700"
            >
              Bekijk foutief beantwoorde vragen uit deze set
            </button>
            <button
              onClick={restartQuiz}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700"
            >
              Start deze set opnieuw
            </button>
          </div>
        </div>
      )}

      {/* Extra Volgende knop net boven de vraagnummering */}
      {!noQuestions && currentQuestionIndex < filteredQuestions.length - 1 && (
        <div className="flex justify-end mb-2">
          <button
            onClick={goToNextQuestion}
            disabled={!hasAnswered}
            className={`px-4 py-2 rounded-lg font-medium ${
              !hasAnswered
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Volgende →
          </button>
        </div>
      )}

      {!noQuestions && (
        <div className="mb-6 flex justify-between items-center">
          <h2 ref={vraagTitelRef} className="text-2xl font-semibold text-blue-700">Multiple Choice Vragen</h2>
          <div className="text-gray-600 flex items-center gap-4">
            Vraag {currentQuestionIndex + 1} van {filteredQuestions.length}
            {currentQuestionIndex === filteredQuestions.length - 1 && !isSubmitted && (
              <button
                onClick={goToNextQuestion}
                disabled={!hasAnswered}
                className={`ml-4 px-4 py-2 rounded-lg font-medium ${
                  !hasAnswered
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Afronden
              </button>
            )}
          </div>
        </div>
      )}

      {noQuestions ? (
        <div className="text-center p-6 bg-red-100 text-red-700 rounded-lg">
          Geen vragen gevonden voor deze combinatie van filters. Pas je filters aan om vragen te zien.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 relative" style={{ minHeight: 320 }}>
            {/* Pogingindicator rechtsboven */}
            <div className="absolute top-2 right-2 text-sm">
              {Array.isArray(scores[currentQuestion.id]) && scores[currentQuestion.id].length > 1 ? (
                <span className="text-blue-600 font-medium">Herhalingen: {scores[currentQuestion.id].length - 1}×</span>
              ) : (
                <span className="text-green-600 font-medium">Eerste keer</span>
              )}
            </div>
            <p className="text-lg font-medium text-gray-800 mb-4">
              {currentQuestion.questionText}
            </p>

            {((showOptions && !showReview) || showReview) && (
              <div className="space-y-3">
                {shuffledOptions[currentQuestion.id]?.options.map((option, optionIndex) => (
                  <label
                    key={optionIndex}
                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      hasAnswered
                        ? optionIndex === shuffledOptions[currentQuestion.id].correctIndex
                          ? 'border-green-500 bg-green-50'
                          : selectedAnswers[currentQuestion.id] === optionIndex
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200'
                        : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value={optionIndex}
                      checked={selectedAnswers[currentQuestion.id] === optionIndex}
                      onChange={() => handleAnswerSelect(currentQuestion.id, optionIndex)}
                      disabled={hasAnswered || showReview}
                      className="mr-3"
                    />
                    <span className={`${
                      hasAnswered && optionIndex === shuffledOptions[currentQuestion.id].correctIndex
                        ? 'text-green-700 font-medium'
                        : hasAnswered && selectedAnswers[currentQuestion.id] === optionIndex
                        ? 'text-red-700'
                        : 'text-gray-700'
                    }`}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {!showOptions && !hasAnswered && !showReview && (
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <button
                  onClick={() => { setShowOptions(true); setTimeout(scrollToVraagTitel, 0); }}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Laat antwoord opties zien
                </button>
              </div>
            )}

            {(hasAnswered || showReview) && (
              <>
                <div className={`mt-4 p-4 rounded-lg ${
                  selectedAnswers[currentQuestion.id] === shuffledOptions[currentQuestion.id].correctIndex
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  <p className="font-medium mb-2">
                    {selectedAnswers[currentQuestion.id] === shuffledOptions[currentQuestion.id].correctIndex
                      ? '✓ Correct!'
                      : '✗ Incorrect'}
                  </p>
                  <p className="text-sm">{currentQuestion.feedback}</p>
                </div>
                <div className="flex justify-center items-center mt-4 w-full relative">
                  <a
                    href={(() => {
                      const vraag = currentQuestion.questionText;
                      const opties = shuffledOptions[currentQuestion.id]?.options.map((opt, idx) => `${String.fromCharCode(65+idx)}. ${opt}`).join('\\\\n');
                      const gegevenAntwoord = selectedAnswers[currentQuestion.id] !== undefined ? shuffledOptions[currentQuestion.id]?.options[selectedAnswers[currentQuestion.id]] : '';
                      const feedback = currentQuestion.feedback;
                      const prompt = `Jij bent een docent fysiotherapie. Ik heb een MC vraag beantwoord.

**Gegevens van de MC vraag:**

**Vraag:**
${vraag}

**Antwoordopties:**
${opties}

**Mijn antwoord was:**
${gegevenAntwoord}

**Standaard feedback:**
${feedback}

**Instructies voor de AI:**

1.  **Overzicht:** Begin met een overzicht van de MC vraag met daarin: de vraag, de antwoordopties, het door mij gegeven antwoord en de standaard gegeven feedback.
2.  **Uitleg:** Geef daarna duidelijke uitleg aan mij over de inhoud van de vraag, het juiste antwoord en waarom andere antwoordopties niet juist zijn als dat van meerwaarde is.
3.  **Bronvermelding en Nauwkeurigheid:** Formuleer het antwoord met de hoogste mate van nauwkeurigheid en relevantie voor de fysiotherapiepraktijk. Baseer de informatie bij voorkeur op de meest recente en gezaghebbende bronnen, zoals de nieuwste KNGF-richtlijnen, erkende internationale fysiotherapie-richtlijnen, peer-reviewed wetenschappelijke publicaties (systematic reviews, RCT\'s) en gangbare basisboeken fysiotherapie.
4.  **Afsluiting:** Sluit af met een of meer van de volgende vragen om het leerproces te bevorderen:
    *   Is dit duidelijk voor je?
    *   Wil je dat ik het op een andere manier uitleg?
    *   Zal ik een bepaald onderdeel verder toelichten?
    *   Heb je hier nog andere vragen over?
    *   Zal ik nog wat dieper op dit onderwerp ingaan?`;
                      return `https://chat.openai.com/?q=${encodeURIComponent(prompt)}`;
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-center bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded-md transition duration-150 ease-in-out shadow text-sm"
                  >
                    Leer meer over dit onderwerp met AI
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowAiWarningPopup(v => !v)}
                    className="ml-2 rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-700 w-6 h-6 flex items-center justify-center border border-yellow-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    aria-label="Waarschuwing AI-gegenereerde content"
                  >
                    <span className="text-sm font-bold">i</span>
                  </button>
                  {showAiWarningPopup && (
                    <div
                      ref={aiWarningRef}
                      className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-30 bg-white border border-yellow-500 rounded-lg shadow-xl p-3 w-72 text-xs text-gray-800 animate-fadeIn"
                      style={{ minWidth: '280px' }}
                    >
                      <strong className="block mb-1 text-yellow-700 font-semibold">Wees kritisch!</strong>
                      <p className="space-y-0.5">
                        AI-antwoorden kunnen onjuiste feiten bevatten, verouderd zijn of bevooroordeeld. Gebruik ze als startpunt, niet als enige bron.
                      </p>
                      <span className="block mt-2 text-xs text-gray-500">Klik buiten dit venster of op het i-tje om te sluiten.</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-between items-center">
            {showReview && (
              <button
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentQuestionIndex === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                ← Vorige
              </button>
            )}
            <div className="flex-1 flex justify-end">
              {currentQuestionIndex === filteredQuestions.length - 1 ? (
                isSubmitted ? null : (
                  <button
                    onClick={goToNextQuestion}
                    disabled={!hasAnswered}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      !hasAnswered
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Afronden
                  </button>
                )
              ) : (
                <button
                  onClick={goToNextQuestion}
                  disabled={!hasAnswered}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    !hasAnswered
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Volgende →
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default McVragenSection; 